/**
 * Axios client for Survey Runtime (FastAPI) — same headers as SDA Azure Functions client.
 */
import axios from 'axios';
import oktaAuth from '../config/okta';
import { getRuntimeApiBaseUrl } from './platformGateway';

export const createRuntimeApiClient = (authState, tenantId = null) => {
  const instance = axios.create({
    baseURL: getRuntimeApiBaseUrl(),
    timeout: 300000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use(
    async (config) => {
      try {
        if (authState?.isAuthenticated) {
          const idTokenObj = await oktaAuth.tokenManager.get('idToken');
          const idToken =
            (typeof idTokenObj?.idToken === 'string' && idTokenObj.idToken) ||
            (typeof idTokenObj?.value === 'string' && idTokenObj.value) ||
            authState?.idToken?.idToken ||
            null;
          if (!idToken) {
            console.error('Authenticated session missing ID token; cannot call Survey Runtime API');
            return Promise.reject(new Error('Missing ID token'));
          }
          config.headers['X-Id-Token'] = idToken;

          const accessToken = await oktaAuth.getAccessToken();
          if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        }

        if (tenantId) {
          config.headers['X-Tenant-ID'] = tenantId;
        }

        if (process.env.REACT_APP_SUBS_KEY) {
          config.headers['Subscription-Key'] = process.env.REACT_APP_SUBS_KEY;
        }

        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      } catch (error) {
        console.error('Runtime API request interceptor error:', error);
        return Promise.reject(error);
      }
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('Unauthorized Survey Runtime access');
        window.location.href = '/signin';
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

export default createRuntimeApiClient;
